import React, { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import AdminHeader from '../../components/admin/AdminHeader';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { adminAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { isOrganizerRole } from '../../utils/roles';

const OrganizerTeamPage = () => {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [meta, setMeta] = useState({ maxTeam: 2, activeCount: 0, remaining: 2 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });

  const canManage = isOrganizerRole(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminAPI.getOrganizerTeam();
      setMembers(res.data?.members || []);
      setMeta({
        maxTeam: res.data?.maxTeam ?? 2,
        activeCount: res.data?.activeCount ?? 0,
        remaining: res.data?.remaining ?? 0
      });
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManage) load();
  }, [canManage, load]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setSaving(true);
    try {
      const email = form.email.trim().toLowerCase();
      const res = await adminAPI.createOrganizerTeamMember({
        name: form.name.trim(),
        email,
        phone: form.phone.trim() || undefined,
        password: form.password.trim() || undefined
      });
      const temp = res.data?.temporaryPassword;
      await load();
      setForm({ name: '', email: '', phone: '', password: '' });
      if (temp) {
        await Swal.fire({
          icon: 'success',
          confirmButtonColor: '#ea580c',
          title: 'Team account created',
          html: `<p class="text-left text-sm mb-2">Share these login details. They can change the password via <strong>Forgot password</strong> (email code).</p>
            <p class="text-left text-sm"><strong>Email:</strong> ${email}</p>
            <p class="text-left text-sm"><strong>Temp password:</strong> <code>${temp}</code></p>`
        });
      } else {
        await Swal.fire({
          icon: 'success',
          confirmButtonColor: '#ea580c',
          title: 'Assigned',
          text: res.message || 'Team member added.'
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        confirmButtonColor: '#ea580c',
        title: 'Could not add',
        text: err.response?.data?.message || err.message
      });
    } finally {
      setSaving(false);
    }
  };

  const onRemove = async (row) => {
    const result = await Swal.fire({
      title: 'Deactivate team member?',
      text: `${row.user?.name || 'They'} will lose access to your events.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ea580c',
      confirmButtonText: 'Deactivate'
    });
    if (!result.isConfirmed) return;
    try {
      await adminAPI.removeOrganizerTeamMember(row.id);
      await load();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Failed',
        text: err.response?.data?.message || err.message,
        confirmButtonColor: '#ea580c'
      });
    }
  };

  if (!canManage) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="lg:pl-72">
          <AdminHeader onMenuClick={() => setSidebarOpen(true)} title="Team" />
          <main className="p-6">
            <p className="text-sm text-gray-600">Only the event owner can manage team accounts.</p>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="lg:pl-72">
        <AdminHeader onMenuClick={() => setSidebarOpen(true)} title="My team" />
        <main className="p-4 sm:p-6 lg:p-8 max-w-5xl">
          <h1 className="text-2xl font-bold text-gray-900">Monitoring team</h1>
          <p className="mt-1 text-sm text-gray-600 mb-6">
            Create up to {meta.maxTeam} staff accounts who can view the same events and ticket sales.
            They sign in at the organizer portal. Password changes use an email code (Forgot password).
          </p>

          {error ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          <div className="grid gap-6 lg:grid-cols-5">
            <form
              onSubmit={onCreate}
              className="lg:col-span-2 rounded-xl border border-orange-100 bg-white p-5 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-gray-900">Add team member</h2>
              <p className="mt-1 text-xs text-gray-500 mb-4">
                {meta.remaining} of {meta.maxTeam} slots remaining. Leave password blank to auto-generate.
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-1">Full name</label>
              <input
                className="w-full mb-3 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                className="w-full mb-3 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                required
              />
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
              <input
                className="w-full mb-3 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              />
              <label className="block text-sm font-medium text-gray-700 mb-1">Password (optional)</label>
              <input
                type="text"
                className="w-full mb-4 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Auto-generate if empty"
                minLength={6}
              />
              <button
                type="submit"
                disabled={saving || loading || meta.remaining <= 0}
                className="w-full rounded-lg bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Create account'}
              </button>
            </form>

            <div className="lg:col-span-3 rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
              <div className="border-b border-gray-100 px-5 py-4">
                <h2 className="text-lg font-semibold text-gray-900">Your team</h2>
              </div>
              {loading ? (
                <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
              ) : members.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500">No team members yet.</div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {members.map((row) => (
                    <li key={row.id} className="px-5 py-4 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-gray-900">{row.user?.name}</p>
                          <span
                            className={`text-xs rounded-full px-2 py-0.5 ${
                              row.isActive
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {row.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 truncate">{row.user?.email}</p>
                      </div>
                      {row.isActive ? (
                        <button
                          type="button"
                          onClick={() => onRemove(row)}
                          className="text-xs font-medium text-red-600 hover:underline"
                        >
                          Deactivate
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default OrganizerTeamPage;
